import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  ExternalLink,
  RefreshCw,
  Package,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import { api } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import clsx from 'clsx';

const PLUGIN_SLUG = 'simple-backup-wp';

type SortField = 'name' | 'status' | 'lastBackupAt' | 'createdAt' | 'url';
type SortOrder = 'asc' | 'desc';

function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);
  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 < p2) return -1;
    if (p1 > p2) return 1;
  }
  return 0;
}

export default function Projects() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['projects', page, statusFilter, search, sortBy, sortOrder],
    queryFn: () =>
      api.projects.list({
        page,
        limit: 20,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: search || undefined,
        sortBy,
        sortOrder,
      }),
  });

  const { data: kumaStatus } = useQuery({
    queryKey: ['kumaAllStatus'],
    queryFn: () => api.settings.getAllKumaStatus(),
    refetchInterval: 60000,
  });

  const { data: pluginStats } = useQuery({
    queryKey: ['plugin-stats', PLUGIN_SLUG],
    queryFn: () => api.plugins.getStats(PLUGIN_SLUG),
  });

  const latestPluginVersion = pluginStats?.latestVersion || null;

  const statusFilters = [
    { value: 'all', label: 'Alle' },
    { value: 'OK', label: 'OK' },
    { value: 'WARNING', label: 'Warnung' },
    { value: 'ERROR', label: 'Fehler' },
    { value: 'UNKNOWN', label: 'Unbekannt' },
  ];

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleRefresh = async () => {
    await refetch();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Projekte</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Projekt hinzufügen
        </button>
      </div>

      <div className="card p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Suchen..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="input pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => {
                  setStatusFilter(filter.value);
                  setPage(1);
                }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw
              className={clsx('w-4 h-4', isFetching && 'animate-spin')}
            />
            {isFetching ? 'Laden...' : 'Aktualisieren'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <SortableHeader
                    label="Status"
                    field="status"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Name"
                    field="name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="URL"
                    field="url"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Plugin
                  </th>
                  <SortableHeader
                    label="Letztes Backup"
                    field="lastBackupAt"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Backups
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Uptime
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data?.projects.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="group relative">
                        <StatusBadge status={project.status} />
                        {project.statusReasons && project.statusReasons.length > 0 && (
                          <div className="absolute left-0 top-full mt-1 z-10 hidden group-hover:block">
                            <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg max-w-xs">
                              <ul className="space-y-1">
                                {project.statusReasons.map((reason, idx) => (
                                  <li key={idx} className="flex items-start gap-1.5">
                                    <span className="text-gray-400 mt-0.5">•</span>
                                    <span>{reason}</span>
                                  </li>
                                ))}
                              </ul>
                              <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 rotate-45"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/projects/${project.id}`}
                        className="font-medium text-primary-600 hover:text-primary-700"
                      >
                        {project.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
                      >
                        {new URL(project.url).hostname}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      {project.pluginVersion ? (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium font-mono ${
                            latestPluginVersion &&
                            compareVersions(
                              project.pluginVersion,
                              latestPluginVersion
                            ) >= 0
                              ? 'bg-green-100 text-green-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}
                          title={
                            latestPluginVersion &&
                            compareVersions(
                              project.pluginVersion,
                              latestPluginVersion
                            ) < 0
                              ? `Update verfügbar: ${latestPluginVersion}`
                              : 'Aktuell'
                          }
                        >
                          <Package className="w-3 h-3" />
                          {project.pluginVersion}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {project.lastBackupAt
                        ? formatDistanceToNow(new Date(project.lastBackupAt), {
                            addSuffix: true,
                            locale: de,
                          })
                        : 'Nie'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {project._count?.backups || 0}
                    </td>
                    <td className="px-4 py-3">
                      {project.kumaMonitorIds?.length > 0 && kumaStatus ? (() => {
                        const statuses = project.kumaMonitorIds
                          .map((mid) => kumaStatus[mid])
                          .filter(Boolean);
                        if (statuses.length === 0) return <span className="text-sm text-gray-400">-</span>;
                        const worstStatus = statuses.some((s) => s.status === 'down')
                          ? 'down'
                          : statuses.some((s) => s.status === 'maintenance')
                          ? 'maintenance'
                          : statuses.every((s) => s.status === 'up')
                          ? 'up'
                          : 'pending';
                        const avgUptime = Math.round(
                          statuses.reduce((sum, s) => sum + s.uptime24h, 0) / statuses.length
                        );
                        return (
                          <div className="flex items-center gap-2">
                            <span
                              className={`w-2.5 h-2.5 rounded-full ${
                                worstStatus === 'up'
                                  ? 'bg-green-500'
                                  : worstStatus === 'down'
                                  ? 'bg-red-500'
                                  : 'bg-gray-400'
                              }`}
                            />
                            <span className="text-sm text-gray-600">
                              {avgUptime}%
                            </span>
                            {statuses.length > 1 && (
                              <span className="bg-gray-100 text-gray-500 text-xs px-1.5 py-0.5 rounded-full">
                                {statuses.length}
                              </span>
                            )}
                          </div>
                        );
                      })() : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/projects/${project.id}`}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
                {data?.projects.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      Keine Projekte gefunden
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data && data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-600">
                Zeige {(page - 1) * 20 + 1} bis{' '}
                {Math.min(page * 20, data.pagination.total)} von{' '}
                {data.pagination.total} Projekten
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  Zurück
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= data.pagination.totalPages}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  Weiter
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showAddModal && (
        <AddProjectModal onClose={() => setShowAddModal(false)} />
      )}
    </div>
  );
}

// Sortable Table Header Component
function SortableHeader({
  label,
  field,
  currentSort,
  sortOrder,
  onSort,
}: {
  label: string;
  field: SortField;
  currentSort: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
}) {
  const isActive = currentSort === field;

  return (
    <th
      className="px-4 py-3 text-left text-sm font-medium text-gray-500 cursor-pointer hover:bg-gray-100 select-none transition-colors"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className="text-gray-400">
          {isActive ? (
            sortOrder === 'asc' ? (
              <ChevronUp className="w-4 h-4 text-primary-600" />
            ) : (
              <ChevronDown className="w-4 h-4 text-primary-600" />
            )
          ) : (
            <ChevronsUpDown className="w-4 h-4 opacity-50" />
          )}
        </span>
      </div>
    </th>
  );
}

function AddProjectModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: api.projects.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    mutation.mutate({ name, url, apiKey });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold mb-4">Neues Projekt</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="Mein WordPress Site"
              required
            />
          </div>
          <div>
            <label className="label">URL</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="input"
              placeholder="https://example.com"
              required
            />
          </div>
          <div>
            <label className="label">API Key</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="input"
              placeholder="Plugin API Key"
              required
            />
          </div>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {error}
            </p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn btn-primary disabled:opacity-50"
            >
              {mutation.isPending ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
